import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TestimonialShowcaseGrid = ({ testimonials, onViewDetails }) => {
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)]?.map((_, index) => (
          <Icon
            key={index}
            name="Star"
            size={14}
            className={index < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600'}
          />
        ))}
      </div>
    );
  };

  if (testimonials?.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
        <Icon name="Search" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Testimonials Found</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">Try adjusting your filters to see more results</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Showing {testimonials?.length} {testimonials?.length === 1 ? 'testimonial' : 'testimonials'}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testimonials?.map((testimonial) => {
          const hasCaseStudy = testimonial?.caseStudyContent && Object.keys(testimonial?.caseStudyContent)?.length > 0;
          
          return (
            <div
              key={testimonial?.id}
              className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Case Study Badge */}
              {hasCaseStudy && (
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2">
                  <div className="flex items-center gap-2 text-white text-sm font-medium">
                    <Icon name="FileText" size={16} />
                    <span>Full Case Study Available</span>
                  </div>
                </div>
              )}

              {/* Media Preview */}
              {testimonial?.testimonialType === 'video' && testimonial?.videoThumbnailUrl && (
                <div className="relative aspect-video bg-gray-900">
                  <img
                    src={testimonial?.videoThumbnailUrl}
                    alt={`Video testimonial from ${testimonial?.companyName}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center">
                      <Icon name="Play" size={28} className="text-white ml-1" />
                    </div>
                  </div>
                  {testimonial?.videoDuration && (
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {testimonial?.videoDuration}
                    </div>
                  )}
                </div>
              )}
              {testimonial?.testimonialType === 'photo' && testimonial?.photoUrl && (
                <div className="aspect-video bg-gray-100 dark:bg-slate-700">
                  <img
                    src={testimonial?.photoUrl}
                    alt={`Photo from ${testimonial?.companyName}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{testimonial?.companyName}</h3>
                      {testimonial?.isVerified && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium rounded">
                          <Icon name="CheckCircle" size={12} />
                          Verified
                        </span>
                      )}
                      {testimonial?.autoPublished && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded">
                          <Icon name="Sparkles" size={12} />
                          AI-Generated
                        </span>
                      )}
                    </div>
                    {renderStars(testimonial?.starRating)}
                  </div>
                </div>

                {/* Display case study executive summary if available */}
                {hasCaseStudy && testimonial?.caseStudyContent?.executive_summary ? (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Executive Summary</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                      {testimonial?.caseStudyContent?.executive_summary}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 line-clamp-3">
                    {testimonial?.reviewText}
                  </p>
                )}

                <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-500">
                  <span className="flex items-center gap-1">
                    <Icon name="DollarSign" size={14} />
                    K{(testimonial?.costSavingsKina || 0)?.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="TrendingUp" size={14} />
                    {testimonial?.timeEfficiencyGainPercentage || 0}%
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Shield" size={14} />
                    {testimonial?.regulatoryAdherenceScore || 0}%
                  </span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{testimonial?.reviewerName}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{testimonial?.reviewerPosition}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onViewDetails(testimonial)}>
                    {hasCaseStudy ? 'View Case Study' : 'View Details'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TestimonialShowcaseGrid;